// src/pages/accounting/ReimbursementsPage.jsx
// Team member expense reimbursement tracking.
// Accessible by all roles; admin features (vouchers, overview) restricted in UI.
// BRIO Migration: Using code "1b" (table compact)

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Upload, X, ChevronUp, ChevronDown, FileText, Check, Download, Link2, Trash2 } from 'lucide-react'; // eslint-disable-line no-unused-vars
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  BrioIconButton,
  BrioDatePicker,
  BrioModal,
  BrioSelect,
  getTablePropsFromCode
} from '../../components/BrioComponents';
import { useSettings } from '../../hooks/useSettings';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../supabaseClient';
import { canViewApprovals, canViewPayouts } from '../../utils/expensePermissions';
import { scanReceiptFile } from '../../utils/ocrReceiptScanner';
import { findBestMatch } from '../../utils/expenseReceiptMatcher';
import useStatementImports from '../../hooks/useStatementImports';
import StatementImportModal from '../../components/StatementImportModal';
import { OFFICE_OVERHEAD_CATEGORIES, PROJECT_EXPENSE_CATEGORIES } from '../../utils/expenseShared';

const PRESENTATION_CODE = '1b';

const OFFICE_EXPENSE_ID = '__office__';

const REIMBURSEMENT_STATUSES = {
  pending: { label: 'Pending', badge: 'brio-badge--pending' },
  approved: { label: 'Approved', badge: 'brio-badge--warning' },
  reimbursed: { label: 'Reimbursed', badge: 'brio-badge--approved' },
  voucher: { label: 'In Voucher', badge: 'brio-badge--info' },
  invoiced: { label: 'Invoiced', badge: 'brio-badge--accent' },
};

// ── Import History Section ──────────────────────────────────────────────────
function ImportHistorySection({ statementImportsHook, supabaseExpenses, reimbursements, activeProjects, activeUsers, formatMoney, displayCurrency, convertMoney, fmtProject }) {
  const getUserName = (userId) => {
    const u = (activeUsers || []).find(u => String(u.id) === String(userId));
    return u?.name || u?.firstName || `User #${userId}`;
  };
  const [sectionExpanded, setSectionExpanded] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [batchExpenses, setBatchExpenses] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [linkingExpenseId, setLinkingExpenseId] = useState(null);

  const { imports, loading } = statementImportsHook;

  const toggleBatch = useCallback(async (batchId) => {
    if (expandedBatch === batchId) {
      setExpandedBatch(null);
      setBatchExpenses([]);
      return;
    }
    setExpandedBatch(batchId);
    setBatchLoading(true);
    const expenses = await statementImportsHook.fetchBatchExpenses(batchId);
    setBatchExpenses(expenses);
    setBatchLoading(false);
  }, [expandedBatch, statementImportsHook]);

  const getProjectName = useCallback((pid) => {
    if (!pid) return 'Office Expense';
    const p = activeProjects.find(x => String(x.id) === String(pid));
    return p ? (fmtProject ? fmtProject(p) : p.name) : `#${pid}`;
  }, [activeProjects, fmtProject]);

  const getReimbursementInfo = useCallback((reimbId) => {
    if (!reimbId) return null;
    return reimbursements.find(r => r.id === reimbId);
  }, [reimbursements]);

  const handleLink = useCallback(async (expenseId, reimbursementId) => {
    if (!supabaseExpenses?.linkExpenseToReimbursement) return;
    const res = await supabaseExpenses.linkExpenseToReimbursement(expenseId, reimbursementId);
    if (res.success && expandedBatch) {
      const expenses = await statementImportsHook.fetchBatchExpenses(expandedBatch);
      setBatchExpenses(expenses);
    }
    setLinkingExpenseId(null);
  }, [supabaseExpenses, expandedBatch, statementImportsHook]);

  const handleUnlink = useCallback(async (expenseId, reimbursementId) => {
    if (!supabaseExpenses?.unlinkExpense) return;
    const res = await supabaseExpenses.unlinkExpense(expenseId, reimbursementId);
    if (res.success && expandedBatch) {
      const expenses = await statementImportsHook.fetchBatchExpenses(expandedBatch);
      setBatchExpenses(expenses);
    }
  }, [supabaseExpenses, expandedBatch, statementImportsHook]);

  const handleStatusToggle = useCallback(async (expenseId, current) => {
    if (!supabaseExpenses?.updateReceiptStatus) return;
    const next = current === 'missing' ? 'not_required' : 'missing';
    const res = await supabaseExpenses.updateReceiptStatus(expenseId, next);
    if (res.success && expandedBatch) {
      const expenses = await statementImportsHook.fetchBatchExpenses(expandedBatch);
      setBatchExpenses(expenses);
    }
  }, [supabaseExpenses, expandedBatch, statementImportsHook]);

  const displayAmt = useCallback((amt, cur, date) => {
    if (cur === displayCurrency) return formatMoney(amt, cur);
    if (convertMoney) return formatMoney(convertMoney(amt, cur, displayCurrency, date), displayCurrency);
    return formatMoney(amt, cur);
  }, [displayCurrency, formatMoney, convertMoney]);

  const sym = displayCurrency === 'EUR' ? '€' : '₹';
  const cellStyle = { fontSize: 11, padding: '4px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  const exportBatchSummary = useCallback((imp, expenses) => {
    const currency = imp.currency || 'EUR';
    const cSym = currency === 'EUR' ? '€' : '₹';
    const fmtAmt = (v) => {
      if (currency === 'EUR') return cSym + (v || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return cSym + (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const projectRows = expenses.filter(e => e.projectId && e._sourceTable !== 'office_overheads_yearly');
    const officeRows = expenses.filter(e => !e.projectId || e._sourceTable === 'office_overheads_yearly');
    const projectTotal = projectRows.reduce((s, e) => s + (e.amountOriginal || e.amount || 0), 0);
    const officeTotal = officeRows.reduce((s, e) => s + (e.amountOriginal || e.amount || 0), 0);

    const escHtml = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const renderTable = (title, items, total) => {
      if (!items.length) return `<p style="color:#888;margin:8px 0;">No ${title.toLowerCase()}.</p>`;
      return `
        <h3 style="font-size:13px;margin:16px 0 6px;color:#1a1a1a;border-bottom:1px solid #d4d0c8;padding-bottom:4px;">${title} (${items.length} items — ${fmtAmt(total)})</h3>
        <table style="width:100%;border-collapse:collapse;font-size:11px;">
          <thead><tr style="background:#f5f3ee;text-align:left;">
            <th style="padding:4px 6px;border:1px solid #d4d0c8;">Date</th>
            <th style="padding:4px 6px;border:1px solid #d4d0c8;">Description</th>
            <th style="padding:4px 6px;border:1px solid #d4d0c8;text-align:right;">Amount</th>
            <th style="padding:4px 6px;border:1px solid #d4d0c8;">Category</th>
            <th style="padding:4px 6px;border:1px solid #d4d0c8;">Project</th>
            <th style="padding:4px 6px;border:1px solid #d4d0c8;">User</th>
            <th style="padding:4px 6px;border:1px solid #d4d0c8;">Receipt</th>
          </tr></thead>
          <tbody>${items.map(e => {
            const reimb = getReimbursementInfo(e.linkedReimbursementId);
            const receiptLabel = e._sourceTable === 'office_overheads_yearly' ? 'Office OH'
              : e.receiptStatus === 'linked' && reimb ? '✓ Linked'
              : e.receiptStatus === 'not_required' ? 'N/A'
              : '✗ Missing';
            const receiptColor = e.receiptStatus === 'linked' ? '#2a7' : e.receiptStatus === 'not_required' ? '#888' : '#c44';
            return `
            <tr>
              <td style="padding:3px 6px;border:1px solid #e5e3de;">${escHtml(e.date)}</td>
              <td style="padding:3px 6px;border:1px solid #e5e3de;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHtml(e.description)}</td>
              <td style="padding:3px 6px;border:1px solid #e5e3de;text-align:right;">${fmtAmt(e.amountOriginal || e.amount)}</td>
              <td style="padding:3px 6px;border:1px solid #e5e3de;">${escHtml(e.category || '—')}</td>
              <td style="padding:3px 6px;border:1px solid #e5e3de;">${escHtml(getProjectName(e.projectId))}</td>
              <td style="padding:3px 6px;border:1px solid #e5e3de;color:#666;">${escHtml(e.userId ? getUserName(e.userId) : '—')}</td>
              <td style="padding:3px 6px;border:1px solid #e5e3de;color:${receiptColor};">${receiptLabel}</td>
            </tr>`;
          }).join('')}
          <tr style="background:#f5f3ee;font-weight:700;">
              <td style="padding:4px 6px;border:1px solid #d4d0c8;" colspan="2">Total</td>
              <td style="padding:4px 6px;border:1px solid #d4d0c8;text-align:right;">${fmtAmt(total)}</td>
              <td style="padding:4px 6px;border:1px solid #d4d0c8;" colspan="4"></td>
            </tr>
          </tbody>
        </table>`;
    };

    const importDate = imp.importedAt?.split('T')[0] || 'unknown';
    const linked = expenses.filter(e => e.receiptStatus === 'linked').length;
    const missing = expenses.filter(e => e.receiptStatus === 'missing' || (!e.receiptStatus && e._sourceTable !== 'office_overheads_yearly')).length;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Import Summary — ${importDate}</title>
      <style>@page{size:A4 landscape;margin:15mm;}body{font-family:'Raleway',Helvetica,Arial,sans-serif;color:#1a1a1a;font-size:12px;}</style>
    </head><body>
      <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #9F902A;padding-bottom:8px;margin-bottom:12px;">
        <div><h1 style="font-size:16px;margin:0;color:#9F902A;">Statement Import Summary</h1>
          <p style="margin:2px 0 0;font-size:11px;color:#888;">Format: ${escHtml(imp.formatLabel)} &bull; Office: ${escHtml(imp.officeLocation)} &bull; Imported: ${importDate}</p></div>
        <div style="text-align:right;font-size:11px;color:#888;">Architecture BRIO</div>
      </div>
      <div style="display:flex;gap:16px;margin-bottom:12px;flex-wrap:wrap;">
        <div style="padding:8px 14px;background:#f5f3ee;flex:1;min-width:120px;"><strong>Total rows:</strong> ${expenses.length}</div>
        <div style="padding:8px 14px;background:#e8f5e9;flex:1;min-width:120px;"><strong>Receipts linked:</strong> ${linked}</div>
        <div style="padding:8px 14px;background:${missing > 0 ? '#fff3e0' : '#f5f3ee'};flex:1;min-width:120px;"><strong>Receipts missing:</strong> ${missing}</div>
      </div>
      ${renderTable('Project Expenses', projectRows, projectTotal)}
      ${renderTable('Office Overheads', officeRows, officeTotal)}
      <p style="font-size:9px;color:#aaa;margin-top:24px;text-align:center;">Generated by TimeTracker Pro — ${new Date().toISOString()}</p>
    </body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import-summary-${importDate}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }, [getProjectName, getReimbursementInfo]);

  // Unlinked reimbursements available for linking
  const availableReceipts = useMemo(
    () => reimbursements.filter(r => !r.linkedExpenseId && r.fileDropboxUrl),
    [reimbursements]
  );

  if (loading) return <div style={{ padding: 16, fontSize: 11, color: '#888' }}>Loading import history...</div>;
  if (!imports.length) return null;

  return (
    <BrioFrame>
      <BrioAnalyticsSurface padding="m">
        <div className="brio-flex brio-items-center brio-justify-between"
          style={{ cursor: 'pointer', marginBottom: sectionExpanded ? 12 : 0 }}
          onClick={() => setSectionExpanded(!sectionExpanded)}>
          <div className="brio-flex brio-items-center brio-gap-xs">
            <span className="brio-font-bold brio-text-s">
              Import History
              <span style={{ fontWeight: 400, color: 'var(--brio-grey)', marginLeft: 6 }}>
                ({imports.length})
              </span>
            </span>
          </div>
          {sectionExpanded
            ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--brio-grey)' }} />
            : <ChevronDown className="w-4 h-4" style={{ color: 'var(--brio-grey)' }} />}
        </div>

        {sectionExpanded && (<>
        <p className="brio-analytics-muted" style={{ fontSize: 10, marginBottom: 10 }}>
          Past statement imports. Expand to review receipt linking status.
        </p>

        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--brio-border, #d4d0c8)', textAlign: 'left' }}>
              <th style={{ padding: '4px 6px', width: '12%' }}>Date</th>
              <th style={{ padding: '4px 6px', width: '14%' }}>Format</th>
              <th style={{ padding: '4px 6px', width: '6%' }}>Office</th>
              <th style={{ padding: '4px 6px', width: '8%', textAlign: 'right' }}>Rows</th>
              <th style={{ padding: '4px 6px', width: '12%', textAlign: 'right' }}>Total ({sym})</th>
              <th style={{ padding: '4px 6px', width: '18%' }}>Receipts</th>
              <th style={{ padding: '4px 6px', width: '26%' }}>Notes</th>
              <th style={{ padding: '4px 6px', width: '4%' }}></th>
            </tr>
          </thead>
          <tbody>
            {imports.map(imp => (
              <React.Fragment key={imp.id}>
                <tr
                  onClick={() => toggleBatch(imp.batchId)}
                  style={{ cursor: 'pointer', borderBottom: '1px solid #eee', background: expandedBatch === imp.batchId ? 'var(--brio-surface-accent, #f5f3e8)' : undefined }}
                  className="brio-row--hoverable"
                >
                  <td style={cellStyle}>{imp.importedAt?.split('T')[0]}</td>
                  <td style={cellStyle}>{imp.formatLabel}</td>
                  <td style={cellStyle}>{imp.officeLocation}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{imp.importedRows}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{displayAmt(imp.brioTotal, imp.currency)}</td>
                  <td style={cellStyle}>
                    {imp.receiptLinkedCount > 0 && <span style={{ color: '#2a7', marginRight: 6 }}>{imp.receiptLinkedCount} linked</span>}
                    {imp.receiptMissingCount > 0 && <span style={{ color: '#c44' }}>{imp.receiptMissingCount} missing</span>}
                    {imp.receiptLinkedCount === 0 && imp.receiptMissingCount === 0 && <span style={{ color: '#888' }}>—</span>}
                  </td>
                  <td style={{ ...cellStyle, color: '#888' }}>{imp.notes || '—'}</td>
                  <td style={cellStyle} className="text-center">
                    <BrioIconButton icon="delete" variant="delete" title="Delete this import and its expenses"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Delete this import (${imp.importedRows} expenses)? This cannot be undone.`)) return;
                        const res = await statementImportsHook.deleteImport(imp.id, imp.batchId);
                        if (!res.success) alert('Delete failed: ' + res.error);
                      }}
                    />
                  </td>
                </tr>
                {expandedBatch === imp.batchId && (
                  <tr>
                    <td colSpan={8} style={{ padding: 0, background: '#fafaf8' }}>
                      {batchLoading ? (
                        <div style={{ padding: 12, fontSize: 11, color: '#888' }}>Loading transactions...</div>
                      ) : (
                        <div style={{ padding: '8px 12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                            <button
                              className="brio-btn brio-btn-secondary"
                              style={{ fontSize: 9, padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
                              onClick={() => exportBatchSummary(imp, batchExpenses)}
                              title="Export import summary as HTML">
                              <Download className="w-3 h-3" /> Export Summary
                            </button>
                          </div>
                          <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                                <th style={{ padding: '3px 4px' }}>Date</th>
                                <th style={{ padding: '3px 4px' }}>Description</th>
                                <th style={{ padding: '3px 4px', textAlign: 'right' }}>Amount</th>
                                <th style={{ padding: '3px 4px' }}>Category</th>
                                <th style={{ padding: '3px 4px' }}>Project</th>
                                <th style={{ padding: '3px 4px' }}>User</th>
                                <th style={{ padding: '3px 4px' }}>Receipt</th>
                                <th style={{ padding: '3px 4px' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {batchExpenses.map(exp => {
                                const isOfficeOverhead = exp._sourceTable === 'office_overheads_yearly';
                                const reimb = getReimbursementInfo(exp.linkedReimbursementId);
                                const isLinked = exp.receiptStatus === 'linked' && reimb;
                                const isNotRequired = exp.receiptStatus === 'not_required';
                                return (
                                  <tr key={`${exp._sourceTable || 'exp'}-${exp.id}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '3px 4px', fontSize: 10 }}>{exp.date}</td>
                                    <td style={{ padding: '3px 4px', fontSize: 10, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                      title={exp.description}>{exp.description}</td>
                                    <td style={{ padding: '3px 4px', fontSize: 10, textAlign: 'right' }}>
                                      {displayAmt(exp.amountOriginal, exp.currencyOriginal, exp.date)}
                                    </td>
                                    <td style={{ padding: '3px 4px', fontSize: 10 }}>{exp.category}</td>
                                    <td style={{ padding: '3px 4px', fontSize: 10 }}>{getProjectName(exp.projectId)}</td>
                                    <td style={{ padding: '3px 4px', fontSize: 10, color: exp.userId ? undefined : '#bbb' }}>
                                      {exp.userId ? getUserName(exp.userId) : '—'}
                                    </td>
                                    <td style={{ padding: '3px 4px', fontSize: 10 }}>
                                      {isOfficeOverhead ? (
                                        <span style={{ color: '#888' }}>Office OH</span>
                                      ) : isLinked ? (
                                        <a href={reimb.fileDropboxUrl} target="_blank" rel="noopener noreferrer"
                                          style={{ color: '#2a7', textDecoration: 'none' }}
                                          title={reimb.fileName}>
                                          <Check className="w-3 h-3" style={{ display: 'inline', verticalAlign: 'middle' }} /> Linked
                                        </a>
                                      ) : isNotRequired ? (
                                        <span style={{ color: '#888' }}>N/A</span>
                                      ) : (
                                        <span style={{ color: '#c44' }}>Missing</span>
                                      )}
                                    </td>
                                    <td style={{ padding: '3px 4px', fontSize: 10 }}>
                                      {isOfficeOverhead ? (
                                        <span style={{ color: '#888', fontSize: 9 }}>—</span>
                                      ) : (
                                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                        {isLinked ? (
                                          <button
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#c44', fontSize: 9 }}
                                            title="Unlink receipt"
                                            onClick={() => handleUnlink(exp.id, exp.linkedReimbursementId)}>
                                            Unlink
                                          </button>
                                        ) : linkingExpenseId === exp.id ? (
                                          <select
                                            autoFocus
                                            style={{ fontSize: 9, padding: '1px 2px', border: '1px solid #d4d0c8', maxWidth: 140 }}
                                            onChange={e => {
                                              if (e.target.value) handleLink(exp.id, Number(e.target.value));
                                              else setLinkingExpenseId(null);
                                            }}
                                            onBlur={() => setLinkingExpenseId(null)}>
                                            <option value="">Select receipt...</option>
                                            {availableReceipts.map(r => (
                                              <option key={r.id} value={r.id}>
                                                {r.expenseDate} — {r.vendor || r.description} ({r.currency} {r.amount})
                                              </option>
                                            ))}
                                          </select>
                                        ) : (
                                          <>
                                            <button
                                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--brio-gold)', fontSize: 9 }}
                                              title="Link to receipt"
                                              onClick={() => setLinkingExpenseId(exp.id)}>
                                              <Link2 className="w-3 h-3" style={{ display: 'inline', verticalAlign: 'middle' }} /> Link
                                            </button>
                                            <button
                                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#888', fontSize: 9 }}
                                              title={isNotRequired ? 'Mark as missing' : 'Mark as not required'}
                                              onClick={() => handleStatusToggle(exp.id, exp.receiptStatus)}>
                                              {isNotRequired ? 'Missing' : 'N/A'}
                                            </button>
                                          </>
                                        )}
                                      </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        </>)}
      </BrioAnalyticsSurface>
    </BrioFrame>
  );
}

export default function ReimbursementsPage({
  currentUser,
  isAdmin,
  activeUsers,
  activeProjects,
  getSortedProjects,
  fmtProject,
  reimbursementsHook,
  invoicesHook,
  supabaseExpenses,
  initialTab,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);
  const settings = useSettings();
  const displayCurrency = settings.displayCurrency || 'INR';
  const convertMoney = settings.convertMoney;
  const formatMoney = settings.formatMoney || ((v) => String(v));
  const displaySymbol = displayCurrency === 'EUR' ? '€' : '₹';

  const {
    reimbursements,
    loading,
    createReimbursement,
    bulkCreateReimbursements,
    updateReimbursement,
    deleteReimbursement,
    createVoucher,
    updateVoucherStatus,
    removeFromVoucher,
    deleteVoucher,
  } = reimbursementsHook;

  const statementImportsHook = useStatementImports();

  // ── Tab state ──
  const showApprovals = canViewApprovals(currentUser);
  const showPayouts = canViewPayouts(currentUser);
  const [activeTab, setActiveTab] = useState(initialTab || 'approvals');

  // ── State ──
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const lastSelectedIdx = useRef(null);
  const [bulkEdit, setBulkEdit] = useState({ userId: '', category: '', projectId: '', officeLocation: '' });
  const [bulkApplying, setBulkApplying] = useState(false);
  const [hiddenIds, setHiddenIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('brio_hidden_reimbursements') || '[]')); } catch { return new Set(); }
  });
  const [showHidden, setShowHidden] = useState(false);

  useEffect(() => {
    try { localStorage.setItem('brio_hidden_reimbursements', JSON.stringify([...hiddenIds])); } catch {}
  }, [hiddenIds]);
  const [voucherSectionExpanded, setVoucherSectionExpanded] = useState(true);
  const [expensesSectionExpanded, setExpensesSectionExpanded] = useState(true);
  const [expandedVoucherId, setExpandedVoucherId] = useState(null);

  // ── Statement import modal ──
  const [showImportModal, setShowImportModal] = useState(false);

  // statementAutoRules is left out of the initial settings fetch because of its
  // size, and only the import modal reads it — fetch it when that modal opens.
  const loadHeavySetting = settings.loadHeavySetting;
  useEffect(() => {
    if (showImportModal) loadHeavySetting?.('statementAutoRules');
  }, [showImportModal, loadHeavySetting]);

  // ── Inline editing state ──
  // editingRows: { [id]: { projectId, userId, expenseDate, vendor, description, category, amount, currency, officeLocation, file, uploading, ocrScanning } }
  // ids starting with "new-" are unsaved rows
  const [editingRows, setEditingRows] = useState({});
  const newRowCounter = useRef(0);

  // Keep the (collapsible) expenses list open whenever there are unsaved new
  // rows — e.g. from an imported statement or a scan — so they aren't hidden
  // behind a collapsed section.
  const pendingNewRowCount = Object.keys(editingRows).filter(id => String(id).startsWith('new-')).length;
  useEffect(() => {
    if (pendingNewRowCount > 0) setExpensesSectionExpanded(true);
  }, [pendingNewRowCount]);
  const scanFileRef = useRef(null);
  const rowFileRefs = useRef({});

  const emptyForm = useCallback(() => ({
    projectId: '',
    userId: currentUser?.id || '',
    expenseDate: new Date().toISOString().split('T')[0],
    vendor: '',
    description: '',
    category: '',
    amount: '',
    currency: 'INR',
    officeLocation: 'IN',
    clientBillable: false,
    file: null,
    uploading: false,
    ocrScanning: false,
  }), [currentUser?.id]);

  // ── Reimburse form (for marking as reimbursed) ──
  const [reimburseModal, setReimburseModal] = useState(null); // reimbursement id
  const [reimburseDate, setReimburseDate] = useState(new Date().toISOString().split('T')[0]);
  const [reimburseMethod, setReimburseMethod] = useState('digital');

  // ── Project list filtered by main project filter ──
  const dropdownProjects = useMemo(
    () => getSortedProjects ? getSortedProjects() : activeProjects,
    [activeProjects, getSortedProjects] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Determine which reimbursements this user can see ──
  const isAdminOrSupervisor = currentUser?.role === 'admin' || currentUser?.role === 'admin-pa' || currentUser?.role === 'supervisor';
  const visibleReimbursements = useMemo(() => {
    if (isAdminOrSupervisor) return reimbursements;
    return reimbursements.filter(r => r.userId === currentUser?.id);
  }, [reimbursements, isAdminOrSupervisor, currentUser?.id]);

  // ── Tab-scoped lists ──
  const approvalsList = useMemo(
    () => reimbursements.filter(r => r.status === 'pending'),
    [reimbursements]
  );
  // Personal expense claims eligible for payout (manual entries, not bank imports)
  const personalPayouts = useMemo(
    () => reimbursements.filter(r =>
      !r.importBatchId &&
      (r.status === 'approved' || r.status === 'reimbursed' || r.status === 'voucher')
    ),
    [reimbursements]
  );

  // Bank-imported project expenses eligible for client voucher grouping.
  // Office expenses (isOfficeExpense=true) are excluded — they live in OfficeExpensesPage.
  const bankImportedPayouts = useMemo(() => {
    const allExpenses = supabaseExpenses?.expenses || [];
    return allExpenses
      .filter(e => e.importBatchId && !e.isOfficeExpense && (e.status === 'planned' || e.status === 'approved' || e.voucherId))
      .map(e => ({
        id:              `exp-${e.id}`,
        _expenseId:      e.id,
        _source:         'expense',
        projectId:       e.projectId,
        userId:          e.userId ?? null,
        expenseDate:     e.date,
        vendor:          e.description || '',
        description:     e.description || '',
        category:        e.category || 'Bank Payment',
        amount:          e.amountOriginal ?? e.amount,
        currency:        e.currencyOriginal || 'INR',
        amountInInr:     e.amountInInr ?? e.amount,
        fxRateToInr:     e.fxRateToInr ?? 1,
        officeLocation:  e.officeLocation,
        isOfficeExpense: e.isOfficeExpense,
        status:          e.voucherId ? 'voucher' : 'approved',
        voucherId:       e.voucherId || null,
        voucherDate:     e.voucherDate || null,
        voucherStatus:   e.voucherStatus || null,
        clientBillable:  e.clientBillable ?? false,
      }));
  }, [supabaseExpenses?.expenses]);

  const payoutsList = useMemo(
    () => [...personalPayouts, ...bankImportedPayouts],
    [personalPayouts, bankImportedPayouts]
  );

  // ── Base list per active tab ──
  const baseList = activeTab === 'approvals' ? approvalsList
    : activeTab === 'payouts' ? payoutsList
    : visibleReimbursements;

  // ── Filter ──
  const filtered = useMemo(() => {
    let list = baseList;
    if (!showHidden) list = list.filter(r => !hiddenIds.has(r.id));
    if (filterMonth) list = list.filter(r => r.expenseDate && r.expenseDate.startsWith(filterMonth));
    if (filterDateFrom) list = list.filter(r => r.expenseDate >= filterDateFrom);
    if (filterDateTo) list = list.filter(r => r.expenseDate <= filterDateTo);
    if (filterProject) list = list.filter(r => String(r.projectId) === String(filterProject));
    if (filterStatus) list = list.filter(r => r.status === filterStatus);
    return list;
  }, [baseList, filterDateFrom, filterDateTo, filterProject, filterStatus, filterMonth, hiddenIds, showHidden]);

  // ── Vouchers (from both reimbursements and bank-imported expenses) ──
  const vouchers = useMemo(() => {
    const map = {};
    reimbursements.forEach(r => {
      if (!r.voucherId) return;
      if (!map[r.voucherId]) map[r.voucherId] = { id: r.voucherId, date: r.voucherDate, status: r.voucherStatus, items: [] };
      map[r.voucherId].items.push({ ...r, _source: 'reimbursement' });
    });
    (supabaseExpenses?.expenses || []).filter(e => !e.isOfficeExpense).forEach(e => {
      if (!e.voucherId) return;
      if (!map[e.voucherId]) map[e.voucherId] = { id: e.voucherId, date: e.voucherDate, status: e.voucherStatus, items: [] };
      map[e.voucherId].items.push({
        ...e,
        id:          `exp-${e.id}`,
        _expenseId:  e.id,
        _source:     'expense',
        expenseDate: e.date,
        vendor:      e.description || '',
        amount:      e.amountOriginal ?? e.amount,
        currency:    e.currencyOriginal || 'INR',
      });
    });
    return Object.values(map);
  }, [reimbursements, supabaseExpenses?.expenses]);

  // ── Expense payments from Payment Inbox (client reimbursements) ──
  const expensePayments = invoicesHook?.expensePayments || [];
  const expensePaymentsLoading = invoicesHook?.expensePaymentsLoading || false;
  const [expenseSectionExpanded, setExpenseSectionExpanded] = useState(true);
  const [editingExpPayments, setEditingExpPayments] = useState({});

  useEffect(() => {
    if (invoicesHook?.fetchExpensePayments) {
      invoicesHook.fetchExpensePayments();
    }
  }, [invoicesHook?.fetchExpensePayments]); // eslint-disable-line react-hooks/exhaustive-deps

  const unlinkedExpensePayments = expensePayments.filter(e => !e.linkedProjectId);
  const linkedExpensePayments = expensePayments.filter(e => e.linkedProjectId);

  const handleLinkToProject = useCallback(async (eventId, projectId) => {
    if (!invoicesHook?.linkExpenseToProject) return;
    const result = await invoicesHook.linkExpenseToProject(eventId, projectId);
    if (!result.success) alert('Failed to link: ' + (result.error || ''));
  }, [invoicesHook]);

  const handleExpPaymentFieldChange = useCallback(async (eventId, field, value) => {
    if (!invoicesHook?.updateExpensePayment) return;
    const result = await invoicesHook.updateExpensePayment(eventId, { [field]: value });
    if (!result.success) alert('Failed to update: ' + (result.error || ''));
  }, [invoicesHook]);

  const handleDeleteExpPayment = useCallback(async (eventId) => {
    if (!invoicesHook?.deleteExpensePayment) return;
    const result = await invoicesHook.deleteExpensePayment(eventId);
    if (!result.success) alert('Failed to delete: ' + (result.error || ''));
  }, [invoicesHook]);

  // ── Inline editing helpers ──
  const updateEditingRow = useCallback((rowId, updates) => {
    setEditingRows(prev => ({ ...prev, [rowId]: { ...prev[rowId], ...updates } }));
  }, []);

  const addEmptyRow = useCallback(() => {
    const id = `new-${++newRowCounter.current}`;
    setEditingRows(prev => ({ ...prev, [id]: emptyForm() }));
  }, [emptyForm]);

  const cancelEditingRow = useCallback((rowId) => {
    setEditingRows(prev => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  }, []);

  const startEditRow = useCallback((r) => {
    setEditingRows(prev => ({
      ...prev,
      [r.id]: {
        projectId: r.projectId ? String(r.projectId) : OFFICE_EXPENSE_ID,
        userId: String(r.userId || ''),
        expenseDate: r.expenseDate || '',
        vendor: r.vendor || '',
        description: r.description || '',
        category: r.category || '',
        amount: String(r.amount || ''),
        currency: r.currency || 'INR',
        officeLocation: r.officeLocation || 'IN',
        clientBillable: r.clientBillable ?? false,
        file: null,
        uploading: false,
        ocrScanning: false,
      },
    }));
  }, []);

  // ── OCR: scan file and insert as new row ──
  const handleScanBill = useCallback(() => {
    if (scanFileRef.current) {
      scanFileRef.current.value = '';
      scanFileRef.current.click();
    }
  }, []);

  const scanSingleFile = useCallback(async (file, id) => {
    try {
      const res = await scanReceiptFile(file);
      if (res?.ok && res.result) {
        const ocr = res.result;
        setEditingRows(prev => {
          if (!prev[id]) return prev;
          const updates = {};
          if (ocr.amount != null) updates.amount = String(ocr.amount);
          if (ocr.currency) updates.currency = ocr.currency;
          if (ocr.date) updates.expenseDate = ocr.date;
          if (ocr.vendor) updates.vendor = ocr.vendor;
          if (ocr.description) updates.description = ocr.description;
          if (ocr.category) updates.category = ocr.category;
          if (ocr.currency === 'EUR') updates.officeLocation = 'NL';
          else if (ocr.currency === 'INR') updates.officeLocation = 'IN';
          return { ...prev, [id]: { ...prev[id], ...updates, ocrScanning: false } };
        });
      } else {
        updateEditingRow(id, { ocrScanning: false });
      }
    } catch (err) {
      console.error('[OCR scan]', err);
      updateEditingRow(id, { ocrScanning: false });
    }
  }, [updateEditingRow]);

  const onScanFileSelected = useCallback(async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const entries = [];
    for (let i = 0; i < files.length; i++) {
      const id = `new-${++newRowCounter.current}`;
      entries.push({ id, file: files[i] });
    }
    setEditingRows(prev => {
      const next = { ...prev };
      for (const { id, file } of entries) {
        next[id] = { ...emptyForm(), file, ocrScanning: true };
      }
      return next;
    });
    // Run OCR on all files concurrently
    await Promise.all(entries.map(({ id, file }) => scanSingleFile(file, id)));
  }, [emptyForm, scanSingleFile]);

  // ── Attach file to an existing editing row ──
  const handleRowFileSelect = useCallback(async (rowId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    updateEditingRow(rowId, { file, ocrScanning: true });
    try {
      const res = await scanReceiptFile(file);
      if (res?.ok && res.result) {
        const ocr = res.result;
        setEditingRows(prev => {
          if (!prev[rowId]) return prev;
          const row = prev[rowId];
          const updates = {};
          if (ocr.amount != null && !row.amount) updates.amount = String(ocr.amount);
          if (ocr.currency && row.currency === 'INR') updates.currency = ocr.currency;
          if (ocr.date && row.expenseDate === new Date().toISOString().split('T')[0]) updates.expenseDate = ocr.date;
          if (ocr.vendor && !row.vendor) updates.vendor = ocr.vendor;
          if (ocr.description && !row.description) updates.description = ocr.description;
          if (ocr.category && !row.category) updates.category = ocr.category;
          if (ocr.currency === 'EUR') updates.officeLocation = 'NL';
          else if (ocr.currency === 'INR') updates.officeLocation = 'IN';
          return { ...prev, [rowId]: { ...prev[rowId], ...updates, ocrScanning: false } };
        });
      } else {
        updateEditingRow(rowId, { ocrScanning: false });
      }
    } catch (err) {
      console.error('[OCR scan]', err);
      updateEditingRow(rowId, { ocrScanning: false });
    }
  }, [updateEditingRow]);

  // ── Upload to Dropbox ──
  const uploadToDropbox = useCallback(async (file, projectCode, expenseDate, vendor, description, officeLocation, isOfficeExpense) => {
    try {
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const base64 = btoa(binary);

      const existingCount = reimbursements.filter(r =>
        String(r.projectId) === String(projectCode) && r.expenseDate === expenseDate
      ).length;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Use manual fetch to avoid Supabase FunctionsClient header construction
      // issues that can cause "not a valid ByteString" errors for some users.
      const safeFileName = file.name.replace(/[^\x20-\x7e]/g, '_');
      const uploadRes = await fetch(`${SUPABASE_URL}/functions/v1/upload-reimbursement-dropbox`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ fileBase64: base64, fileName: safeFileName, projectCode, expenseDate, vendor: vendor || '', description: description || 'receipt', sequenceNumber: existingCount + 1, officeLocation: officeLocation || 'IN', isOfficeExpense: !!isOfficeExpense }),
      });

      if (!uploadRes.ok) {
        let detail = '';
        try {
          const errData = await uploadRes.json();
          detail = errData?.error || errData?.message || '';
        } catch { /* ignore */ }
        throw new Error(detail || `Upload failed (${uploadRes.status})`);
      }
      const result = await uploadRes.json();
      if (!result?.success) throw new Error(result?.error || 'Upload failed');
      return { fileName: result.fileName, fileDropboxPath: result.dropboxPath, fileDropboxUrl: result.dropboxUrl };
    } catch (err) {
      console.error('[Dropbox upload]', err);
      alert('File upload failed: ' + err.message + '\nThe expense will be saved without the file.');
      return null;
    }
  }, [reimbursements]);

  // ── Save a row (new or existing) ──
  const saveRow = useCallback(async (rowId) => {
    const row = editingRows[rowId];
    if (!row) return;

    const amount = parseFloat(row.amount);
    if (!Number.isFinite(amount) || amount <= 0) { alert('Please enter a valid amount'); return; }
    if (!row.projectId) { alert('Please select a project or Office Expense'); return; }
    if (!row.userId) { alert('Please select a team member'); return; }

    const isOfficeExpense = row.projectId === OFFICE_EXPENSE_ID;

    updateEditingRow(rowId, { uploading: true });

    const project = isOfficeExpense ? null : activeProjects.find(p => String(p.id) === String(row.projectId));
    const projectCode = (isOfficeExpense ? 'OFFICE' : (project?.projectCode || project?.name?.substring(0, 8).toUpperCase() || 'PROJ')).replace(/[^\x20-\x7e]/g, '_');

    let fileData = {};
    if (row.file) {
      const result = await uploadToDropbox(row.file, projectCode, row.expenseDate, row.vendor, row.description, row.officeLocation, isOfficeExpense);
      if (result) fileData = result;
    }

    const data = {
      projectId: isOfficeExpense ? null : Number(row.projectId),
      userId: Number(row.userId),
      expenseDate: row.expenseDate,
      vendor: row.vendor,
      description: row.description,
      category: row.category || 'Miscellaneous',
      amount,
      currency: row.currency,
      amountInInr: row.currency === 'EUR' && settings.getFxRateForDate
        ? Math.round(amount * (settings.getFxRateForDate(row.expenseDate) || 1) * 100) / 100
        : amount,
      fxRateToInr: row.currency === 'EUR' && settings.getFxRateForDate
        ? settings.getFxRateForDate(row.expenseDate) || 1
        : 1,
      officeLocation: row.officeLocation,
      clientBillable: row.clientBillable ?? false,
      ...fileData,
    };

    const isNew       = String(rowId).startsWith('new-');
    const isExpenseRow = String(rowId).startsWith('exp-');
    let result;
    if (isNew) {
      result = await createReimbursement(data);
    } else if (isExpenseRow) {
      const expenseId = Number(rowId.replace('exp-', ''));
      result = await supabaseExpenses.updateExpense(expenseId, {
        category:       data.category,
        description:    data.description,
        amount:         data.amount,
        date:           data.expenseDate,
        officeLocation: data.officeLocation,
        amountInInr:    data.amountInInr,
        fxRateToInr:    data.fxRateToInr,
        clientBillable: data.clientBillable,
        ...fileData,
      });
    } else {
      result = await updateReimbursement(rowId, data);
    }

    if (!result.success) {
      alert('Error: ' + (result.error || 'Unknown'));
      updateEditingRow(rowId, { uploading: false });
      return;
    }

    // Auto-link to unlinked statement expense if match found
    if (isNew && result.data && supabaseExpenses?.linkExpenseToReimbursement) {
      try {
        const unlinkedExpenses = (supabaseExpenses.expenses || []).filter(e => e.importBatchId && !e.linkedReimbursementId);
        const saved = result.data;
        const { reimbursement: _, score } = findBestMatch(
          { amount: data.amountInInr, amountInInr: data.amountInInr, date: data.expenseDate, description: data.description, category: data.category },
          [] // not matching against reimbursements — we're matching expenses against this reimbursement
        );
        // Reverse match: find best expense for this reimbursement
        let bestExp = null, bestScore = 0;
        for (const exp of unlinkedExpenses) {
          const s = (() => {
            let sc = 0;
            const expAmt = Number(exp.amountInInr || exp.amount || 0);
            const rAmt = Number(data.amountInInr || data.amount || 0);
            if (expAmt > 0 && rAmt > 0 && Math.abs(expAmt - rAmt) <= 0.01 * Math.max(expAmt, rAmt)) sc += 50;
            const days = Math.abs(Math.round((new Date(exp.date) - new Date(data.expenseDate)) / 86400000));
            if (days === 0) sc += 30; else if (days <= 3) sc += 15;
            const expText = `${exp.description || ''} ${exp.category || ''}`.toLowerCase();
            const rText = `${data.vendor || ''} ${data.description || ''}`.toLowerCase();
            const tokens = rText.split(/\s+/).filter(t => t.length > 2);
            if (tokens.some(t => expText.includes(t))) sc += 20;
            return sc;
          })();
          if (s > bestScore) { bestScore = s; bestExp = exp; }
        }
        if (bestExp && bestScore >= 65) {
          await supabaseExpenses.linkExpenseToReimbursement(bestExp.id, saved.id || saved);
        }
      } catch (linkErr) {
        console.warn('[Reimbursement] Auto-link failed:', linkErr.message);
      }
    }

    cancelEditingRow(rowId);
  }, [editingRows, activeProjects, settings, createReimbursement, updateReimbursement, uploadToDropbox, updateEditingRow, cancelEditingRow, supabaseExpenses]);

  // ── Mark as reimbursed ──
  const handleReimburse = async () => {
    if (!reimburseModal) return;
    const result = await updateReimbursement(reimburseModal, {
      status: 'reimbursed',
      reimbursedDate: reimburseDate,
      reimbursedMethod: reimburseMethod,
      reimbursedBy: currentUser?.id,
    });
    if (!result.success) alert('Error: ' + result.error);
    setReimburseModal(null);
  };

  // ── Create voucher from selected (spans reimbursements + expenses) ──
  const handleCreateVoucher = async () => {
    if (selectedIds.size === 0) return;
    const selectedItems = payoutsList.filter(r => selectedIds.has(r.id));
    const reimbIds  = selectedItems.filter(r => r._source !== 'expense').map(r => r.id);
    const expIds    = selectedItems.filter(r => r._source === 'expense').map(r => r._expenseId);
    const voucherDate = new Date().toISOString().split('T')[0];
    // Use a shared voucher ID so both tables carry the same grouping key
    const sharedVoucherId = `VCH-${Date.now().toString(36).toUpperCase()}`;

    let ok = true;
    if (reimbIds.length > 0) {
      const r1 = await createVoucher(reimbIds, { date: voucherDate, voucherId: sharedVoucherId });
      if (!r1.success) { ok = false; alert('Error (reimbursements): ' + r1.error); }
    }
    if (expIds.length > 0) {
      const r2 = await supabaseExpenses.createExpenseVoucher(expIds, { date: voucherDate, voucherId: sharedVoucherId });
      if (!r2.success) { ok = false; alert('Error (expenses): ' + r2.error); }
    }
    if (ok) { setSelectedIds(new Set()); setShowVoucherModal(false); }
  };

  // ── Helpers ──
  const getUserName = (userId) => {
    const user = activeUsers.find(u => String(u.id) === String(userId));
    return user?.name || user?.firstName || `User #${userId}`;
  };

  const getProjectName = (projectId) => {
    if (!projectId) return 'Office Expense';
    const p = activeProjects.find(x => String(x.id) === String(projectId));
    return p ? fmtProject(p) : `#${projectId}`;
  };

  const displayAmt = (r) => {
    // Always convert from original currency to avoid broken amountInInr for EUR claims
    const amt = Number(r.amount || 0);
    const fromCurrency = r.currency || 'INR';
    return convertMoney ? convertMoney(amt, fromCurrency, displayCurrency, r.expenseDate) : amt;
  };

  // Show checkboxes in both payouts tab (for voucher creation) and approvals tab (for bulk approve)
  const showVoucherCheckbox = isAdmin && (activeTab === 'payouts' || activeTab === 'approvals');

  const toggleSelect = (id, rowIdx, shiftHeld) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (shiftHeld && lastSelectedIdx.current !== null) {
        const start = Math.min(lastSelectedIdx.current, rowIdx);
        const end   = Math.max(lastSelectedIdx.current, rowIdx);
        for (let i = start; i <= end; i++) {
          const row = sortedFiltered[i];
          if (row && (row.status === 'pending' || row.status === 'approved' || row.status === 'reimbursed')) {
            next.add(row.id);
          }
        }
      } else {
        if (next.has(id)) next.delete(id); else next.add(id);
      }
      return next;
    });
    if (!shiftHeld) lastSelectedIdx.current = rowIdx;
  };

  const handleSortClick = (key) => setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  const getSortIndicator = (key) => sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : '';

  const sortedFiltered = useMemo(() => {
    const { key, direction } = sortConfig;
    return [...filtered].sort((a, b) => {
      let valA, valB;
      switch (key) {
        case 'date':
          valA = a.expenseDate || ''; valB = b.expenseDate || '';
          break;
        case 'name':
          valA = getUserName(a.userId).toLowerCase(); valB = getUserName(b.userId).toLowerCase();
          break;
        case 'project':
          valA = getProjectName(a.projectId).toLowerCase(); valB = getProjectName(b.projectId).toLowerCase();
          break;
        case 'vendor':
          valA = (a.vendor || '').toLowerCase(); valB = (b.vendor || '').toLowerCase();
          break;
        case 'description':
          valA = (a.description || '').toLowerCase(); valB = (b.description || '').toLowerCase();
          break;
        case 'category':
          valA = (a.category || '').toLowerCase(); valB = (b.category || '').toLowerCase();
          break;
        case 'office':
          valA = (a.officeLocation || '').toLowerCase(); valB = (b.officeLocation || '').toLowerCase();
          break;
        case 'amount':
          valA = a.amount ?? 0; valB = b.amount ?? 0;
          return direction === 'asc' ? valA - valB : valB - valA;
        case 'status':
          valA = (a.status || '').toLowerCase(); valB = (b.status || '').toLowerCase();
          break;
        default: return 0;
      }
      return direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
  }, [filtered, sortConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkApply = useCallback(async () => {
    const updates = {};
    if (bulkEdit.userId)         updates.userId         = Number(bulkEdit.userId);
    if (bulkEdit.category)       updates.category       = bulkEdit.category;
    if (bulkEdit.projectId)      updates.projectId      = Number(bulkEdit.projectId);
    if (bulkEdit.officeLocation) updates.officeLocation = bulkEdit.officeLocation;
    if (Object.keys(updates).length === 0 || selectedIds.size === 0) return;
    setBulkApplying(true);
    for (const id of selectedIds) {
      const isExpenseRow = String(id).startsWith('exp-');
      if (isExpenseRow) {
        const expenseId = Number(String(id).replace('exp-', ''));
        const expUpdates = {};
        if (updates.userId)         expUpdates.userId         = updates.userId;
        if (updates.category)       expUpdates.category       = updates.category;
        if (updates.officeLocation) expUpdates.officeLocation = updates.officeLocation;
        if (updates.projectId)      expUpdates.projectId      = updates.projectId;
        if (Object.keys(expUpdates).length > 0) await supabaseExpenses.updateExpense(expenseId, expUpdates);
      } else {
        await updateReimbursement(id, updates);
      }
    }
    setBulkApplying(false);
    setSelectedIds(new Set());
    setBulkEdit({ userId: '', category: '', projectId: '', officeLocation: '' });
  }, [selectedIds, bulkEdit, updateReimbursement, supabaseExpenses]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkApprove = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setBulkApplying(true);
    let anyError = false;
    for (const id of selectedIds) {
      const result = await updateReimbursement(id, { status: 'approved' });
      if (!result.success) { anyError = true; console.error('Approve error:', result.error); }
    }
    setBulkApplying(false);
    setSelectedIds(new Set());
    if (anyError) alert('One or more items could not be approved. Check the console for details.');
  }, [selectedIds, updateReimbursement]); // eslint-disable-line react-hooks/exhaustive-deps

  const cellStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0, fontSize: '10px' };

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font" style={{ background: '#fff' }}>

      {/* PAGE HEADER BAND (tabs + filters in one band, single bottom border) */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 0 }}>
            {/* Tab row — hidden when embedded inside ExpenseAdminPage (initialTab locks the view) */}
            {!initialTab && (
            <div className="brio-flex" style={{ gap: 0, marginBottom: 8 }}>
              {[
                { id: 'approvals', label: 'Approvals', count: approvalsList.length, show: showApprovals },
                { id: 'payouts', label: 'Payouts', count: payoutsList.length, show: showPayouts },
              ].filter(t => t.show).map(tab => (
                <button key={tab.id}
                  style={{
                    fontSize: '11px', padding: '8px 16px', borderRadius: 0,
                    border: 'none',
                    borderBottom: activeTab === tab.id ? '2px solid var(--brio-gold, #9F902A)' : '2px solid transparent',
                    background: 'none',
                    color: activeTab === tab.id ? 'var(--brio-black, #1a1a1a)' : 'var(--brio-grey, #888)',
                    fontWeight: activeTab === tab.id ? 500 : 400,
                    fontFamily: 'var(--brio-font-family)',
                    cursor: 'pointer',
                  }}
                  onClick={() => setActiveTab(tab.id)}>
                  {tab.label}
                  {tab.count > 0 && (
                    <span style={{ marginLeft: 4, fontSize: '9px', opacity: 0.7 }}>({tab.count})</span>
                  )}
                </button>
              ))}
            </div>
            )}
            {/* Filter row */}
            <div className="brio-flex brio-items-center brio-gap-xs" style={{ flexWrap: 'nowrap' }}>
                  <BrioSelect value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
                    style={{ padding: '4px 8px', fontSize: '11px', minWidth: 100 }}>
                    <option value="">All Months</option>
                    {(() => {
                      const months = [];
                      const now = new Date();
                      const start = new Date(2025, 0, 1);
                      const end = new Date(now.getFullYear(), now.getMonth(), 1);
                      for (let d = new Date(end); d >= start; d.setMonth(d.getMonth() - 1)) {
                        const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        const label = d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
                        months.push(<option key={val} value={val}>{label}</option>);
                      }
                      return months;
                    })()}
                  </BrioSelect>
                  <BrioDatePicker value={filterDateFrom} onChange={setFilterDateFrom}
                    className="brio-datepicker-trigger--compact" placeholder="From"
                    style={{ width: 110, fontSize: '10px' }} clearable />
                  <BrioDatePicker value={filterDateTo} onChange={setFilterDateTo}
                    className="brio-datepicker-trigger--compact" placeholder="To"
                    style={{ width: 110, fontSize: '10px' }} clearable />
                  {isAdminOrSupervisor && (
                    <BrioSelect value={filterProject} onChange={e => setFilterProject(e.target.value)}
                      style={{ padding: '4px 8px', fontSize: '11px' }}>
                      <option value="">All Projects</option>
                      {dropdownProjects.map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
                    </BrioSelect>
                  )}
                  <BrioSelect value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                    style={{ padding: '4px 8px', fontSize: '11px' }}>
                    <option value="">All Statuses</option>
                    {Object.entries(REIMBURSEMENT_STATUSES).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </BrioSelect>
                  {hiddenIds.size > 0 && (
                    <button
                      className="brio-btn brio-btn-secondary"
                      style={{ fontSize: '11px', padding: '5px 10px', background: showHidden ? 'var(--brio-gold, #9F902A)' : undefined, color: showHidden ? '#fff' : undefined }}
                      onClick={() => setShowHidden(v => !v)}
                      title={showHidden ? 'Hide the hidden rows' : `Show ${hiddenIds.size} hidden row(s)`}>
                      {showHidden ? `Showing ${hiddenIds.size} hidden` : `${hiddenIds.size} hidden`}
                    </button>
                  )}
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexShrink: 0 }}>
                    {isAdmin && selectedIds.size > 0 && activeTab === 'approvals' && (
                      <button className="brio-btn brio-btn-primary brio-flex brio-items-center brio-gap-xs"
                        style={{ fontSize: '11px', padding: '5px 10px' }}
                        onClick={handleBulkApprove}>
                        <Check className="w-3 h-3" /> Approve ({selectedIds.size})
                      </button>
                    )}
                    {isAdmin && selectedIds.size > 0 && activeTab === 'payouts' && (
                      <button className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                        style={{ fontSize: '11px', padding: '5px 10px' }}
                        onClick={() => setShowVoucherModal(true)}>
                        Create Voucher ({selectedIds.size})
                      </button>
                    )}
                    {isAdmin && activeTab === 'payouts' && (
                      <button className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                        style={{ fontSize: '11px', padding: '5px 10px' }}
                        onClick={() => setShowImportModal(true)}>
                        <Upload className="w-3 h-3" /> Import Statement
                      </button>
                    )}
                  </div>
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          {/* ── Bulk Edit Toolbar ── */}
          {showVoucherCheckbox && selectedIds.size > 0 && activeTab === 'payouts' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginBottom: 4, background: 'var(--brio-surface-accent, #f5f3e8)', borderRadius: 6, border: '1px solid var(--brio-gold, #9F902A)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--brio-gold, #9F902A)', whiteSpace: 'nowrap', marginRight: 4 }}>
                {selectedIds.size} selected
              </span>
              <span style={{ fontSize: 10, color: '#888', marginRight: 4 }}>Apply to all:</span>

              <select value={bulkEdit.userId}
                onChange={e => setBulkEdit(p => ({ ...p, userId: e.target.value }))}
                className="brio-btn brio-btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }}>
                <option value="">— User —</option>
                {activeUsers.map(u => <option key={u.id} value={u.id}>{u.name || u.firstName}</option>)}
              </select>

              <select value={bulkEdit.category}
                onChange={e => setBulkEdit(p => ({ ...p, category: e.target.value }))}
                className="brio-btn brio-btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }}>
                <option value="">— Category —</option>
                <optgroup label="Project">
                  {PROJECT_EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </optgroup>
                <optgroup label="Office">
                  {OFFICE_OVERHEAD_CATEGORIES.map(c => <option key={`o-${c}`} value={c}>{c}</option>)}
                </optgroup>
              </select>

              <select value={bulkEdit.projectId}
                onChange={e => setBulkEdit(p => ({ ...p, projectId: e.target.value }))}
                className="brio-btn brio-btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }}>
                <option value="">— Project —</option>
                {dropdownProjects.map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
              </select>

              <select value={bulkEdit.officeLocation}
                onChange={e => setBulkEdit(p => ({ ...p, officeLocation: e.target.value }))}
                className="brio-btn brio-btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }}>
                <option value="">— Office —</option>
                <option value="IN">IN</option>
                <option value="NL">NL</option>
              </select>

              <button className="brio-btn brio-btn-primary"
                style={{ fontSize: 10, padding: '2px 10px' }}
                disabled={bulkApplying || (!bulkEdit.userId && !bulkEdit.category && !bulkEdit.projectId && !bulkEdit.officeLocation)}
                onClick={handleBulkApply}>
                {bulkApplying ? 'Applying…' : 'Apply'}
              </button>

              <button className="brio-btn brio-btn-secondary"
                style={{ fontSize: 10, padding: '2px 10px', borderColor: '#aaa', color: '#666' }}
                title="Hide selected rows from view (toggle with Show hidden)"
                onClick={() => {
                  setHiddenIds(prev => {
                    const next = new Set(prev);
                    selectedIds.forEach(id => next.add(id));
                    try { localStorage.setItem('brio_hidden_reimbursements', JSON.stringify([...next])); } catch {}
                    return next;
                  });
                  setSelectedIds(new Set());
                  setBulkEdit({ userId: '', category: '', projectId: '', officeLocation: '' });
                }}>
                Hide selected
              </button>

              <button className="brio-btn brio-btn-secondary"
                style={{ fontSize: 10, padding: '2px 8px', marginLeft: 'auto' }}
                onClick={() => { setSelectedIds(new Set()); setBulkEdit({ userId: '', category: '', projectId: '', officeLocation: '' }); }}>
                Clear selection
              </button>
            </div>
          )}

          <BrioAnalyticsSurface padding="m">
            {/* ── Expenses Table (collapsible) ── */}
            <div className="brio-flex brio-items-center brio-justify-between"
              style={{ cursor: 'pointer', marginBottom: expensesSectionExpanded ? 12 : 0 }}
              onClick={() => setExpensesSectionExpanded(!expensesSectionExpanded)}>
              <div className="brio-flex brio-items-center brio-gap-xs">
                <span className="brio-font-bold brio-text-s">
                  Expenses
                  <span style={{ fontWeight: 400, color: 'var(--brio-grey)', marginLeft: 6 }}>
                    ({filtered.length})
                  </span>
                </span>
              </div>
              <div className="brio-flex brio-items-center brio-gap-xs">
                {!expensesSectionExpanded && filtered.length > 0 && (
                  <span className="brio-text-grey" style={{ fontSize: 11 }}>
                    Total: {formatMoney(filtered.reduce((s, r) => s + displayAmt(r), 0), displayCurrency, { exact: true })}
                  </span>
                )}
                {expensesSectionExpanded
                  ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--brio-grey)' }} />
                  : <ChevronDown className="w-4 h-4" style={{ color: 'var(--brio-grey)' }} />}
              </div>
            </div>

            {expensesSectionExpanded && (<>
            {loading ? (
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '32px 0' }}>Loading...</div>
            ) : (
              <BrioByCode code={PRESENTATION_CODE} className="!overflow-visible">
                <BrioTable {...tableProps} style={{ tableLayout: 'fixed', width: '100%' }}>
                  <colgroup>
                    {showVoucherCheckbox && <col style={{ width: '3%' }} />}
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '4%' }} />
                    <col style={{ width: '3%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '4%' }} />
                    <col style={{ width: '9%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      {showVoucherCheckbox && <th></th>}
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('date')}>Date{getSortIndicator('date')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('name')}>Name{getSortIndicator('name')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('project')}>Project{getSortIndicator('project')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('vendor')}>Vendor{getSortIndicator('vendor')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('description')}>Description{getSortIndicator('description')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('category')}>Category{getSortIndicator('category')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('office')}>Office{getSortIndicator('office')}</th>
                      <th className="text-center" title="Client-billable: can be invoiced to the client">Bill</th>
                      <th className="text-right" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('amount')}>Amount ({displaySymbol}){getSortIndicator('amount')}</th>
                      <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('status')}>Status{getSortIndicator('status')}</th>
                      <th className="text-center">File</th>
                      <th className="text-center col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* ── New / editing rows at top ── */}
                    {Object.entries(editingRows).filter(([id]) => String(id).startsWith('new-')).map(([rowId, row]) => (
                      <tr key={rowId} style={{ background: 'var(--brio-surface-accent, #f5f3e8)' }}>
                        {showVoucherCheckbox && <td></td>}
                        <td>
                          <input type="date" value={row.expenseDate}
                            style={{ fontSize: 10, padding: '2px 4px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                            onChange={e => updateEditingRow(rowId, { expenseDate: e.target.value })} />
                        </td>
                        <td>
                          <select value={row.userId}
                            style={{ fontSize: 10, padding: '2px 4px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                            onChange={e => updateEditingRow(rowId, { userId: e.target.value })}>
                            <option value="">—</option>
                            {activeUsers.filter(u => u.status === 'active').map(u => (
                              <option key={u.id} value={u.id}>{u.name || u.firstName}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select value={row.projectId}
                            style={{ fontSize: 10, padding: '2px 4px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                            onChange={e => {
                              const pid = e.target.value;
                              if (pid === OFFICE_EXPENSE_ID) {
                                updateEditingRow(rowId, { projectId: pid, category: '' });
                              } else {
                                const proj = activeProjects.find(p => String(p.id) === String(pid));
                                const cur = proj?.contractCurrency || 'INR';
                                updateEditingRow(rowId, { projectId: pid, category: '', currency: cur, officeLocation: cur === 'EUR' ? 'NL' : 'IN' });
                              }
                            }}>
                            <option value="">Select...</option>
                            <option value={OFFICE_EXPENSE_ID}>Office Expense</option>
                            <option disabled>──────────</option>
                            {dropdownProjects.map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
                          </select>
                        </td>
                        <td>
                          <input type="text" value={row.vendor} placeholder="Vendor"
                            style={{ fontSize: 10, padding: '2px 3px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                            onChange={e => updateEditingRow(rowId, { vendor: e.target.value })} />
                        </td>
                        <td>
                          <input type="text" value={row.description} placeholder="Description"
                            style={{ fontSize: 10, padding: '2px 3px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                            onChange={e => updateEditingRow(rowId, { description: e.target.value })} />
                        </td>
                        <td>
                          <select value={row.category}
                            style={{ fontSize: 10, padding: '2px 4px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                            onChange={e => updateEditingRow(rowId, { category: e.target.value })}>
                            <option value="">—</option>
                            {(row.projectId === OFFICE_EXPENSE_ID ? OFFICE_OVERHEAD_CATEGORIES : PROJECT_EXPENSE_CATEGORIES).map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td>
                          <select value={row.officeLocation}
                            style={{ fontSize: 10, padding: '2px 3px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                            onChange={e => updateEditingRow(rowId, { officeLocation: e.target.value })}>
                            <option value="NL">NL</option>
                            <option value="IN">IN</option>
                          </select>
                        </td>
                        <td className="text-center">
                          <input type="checkbox" className="brio-checkbox"
                            checked={row.clientBillable || false}
                            title="Client-billable"
                            disabled={!row.projectId || row.projectId === OFFICE_EXPENSE_ID}
                            onChange={e => updateEditingRow(rowId, { clientBillable: e.target.checked })} />
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <select value={row.currency}
                              style={{ fontSize: 9, padding: '2px', width: 36, flexShrink: 0, border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                              onChange={e => updateEditingRow(rowId, { currency: e.target.value })}>
                              <option value="INR">₹</option>
                              <option value="EUR">€</option>
                            </select>
                            <input type="text" inputMode="decimal" value={row.amount} placeholder="0"
                              style={{ fontSize: 11, padding: '2px 4px', width: '100%', textAlign: 'right', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                              onChange={e => updateEditingRow(rowId, { amount: e.target.value.replace(',', '.') })} />
                          </div>
                        </td>
                        <td className="text-center">
                          {row.ocrScanning ? (
                            <span style={{ fontSize: 9, color: 'var(--brio-gold)' }}>Scanning...</span>
                          ) : (
                            <span className="brio-badge brio-badge--pending" style={{ fontSize: '9px', padding: '1px 5px' }}>New</span>
                          )}
                        </td>
                        <td className="text-center">
                          {row.file ? (
                            <span title={row.file.name} style={{ color: 'var(--brio-gold)', cursor: 'default' }}>
                              <FileText className="w-3 h-3" />
                            </span>
                          ) : (
                            <>
                              <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--brio-grey)' }}
                                title="Attach file"
                                onClick={() => {
                                  if (!rowFileRefs.current[rowId]) {
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = 'image/*,.pdf';
                                    input.onchange = (e) => handleRowFileSelect(rowId, e);
                                    rowFileRefs.current[rowId] = input;
                                  }
                                  rowFileRefs.current[rowId].value = '';
                                  rowFileRefs.current[rowId].click();
                                }}>
                                <Upload className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </td>
                        <td className="text-center">
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                            <button className="brio-btn brio-btn-primary" style={{ padding: '2px 8px', fontSize: '9px' }}
                              onClick={() => saveRow(rowId)} disabled={row.uploading || row.ocrScanning}>
                              {row.uploading ? '...' : 'Save'}
                            </button>
                            <button className="brio-btn brio-btn-secondary" style={{ padding: '2px 6px', fontSize: '9px' }}
                              onClick={() => cancelEditingRow(rowId)}>
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* ── Existing rows (inline-editable if editing) ── */}
                    {filtered.length === 0 && Object.keys(editingRows).length === 0 && (
                      <tr><td colSpan={showVoucherCheckbox ? 13 : 12} className="brio-text-center brio-analytics-muted" style={{ padding: '24px 0' }}>
                        No reimbursements found.
                      </td></tr>
                    )}
                    {sortedFiltered.map((r, rowIdx) => {
                      const isEditing = editingRows[r.id];
                      const row = editingRows[r.id];
                      const statusInfo = REIMBURSEMENT_STATUSES[r.status] || REIMBURSEMENT_STATUSES.pending;

                      if (isEditing) {
                        return (
                          <tr key={r.id} style={{ background: 'var(--brio-surface-accent, #f5f3e8)' }}>
                            {showVoucherCheckbox && (
                              <td></td>
                            )}
                            <td>
                              <input type="date" value={row.expenseDate}
                                style={{ fontSize: 10, padding: '2px 4px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                                onChange={e => updateEditingRow(r.id, { expenseDate: e.target.value })} />
                            </td>
                            <td>
                              <select value={row.userId}
                                style={{ fontSize: 10, padding: '2px 4px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                                onChange={e => updateEditingRow(r.id, { userId: e.target.value })}>
                                <option value="">—</option>
                                {activeUsers.filter(u => u.status === 'active').map(u => (
                                  <option key={u.id} value={u.id}>{u.name || u.firstName}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select value={row.projectId}
                                style={{ fontSize: 10, padding: '2px 4px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                                onChange={e => {
                                  const pid = e.target.value;
                                  if (pid === OFFICE_EXPENSE_ID) {
                                    updateEditingRow(r.id, { projectId: pid, category: '' });
                                  } else {
                                    const proj = activeProjects.find(p => String(p.id) === String(pid));
                                    const cur = proj?.contractCurrency || 'INR';
                                    updateEditingRow(r.id, { projectId: pid, category: '', currency: cur, officeLocation: cur === 'EUR' ? 'NL' : 'IN' });
                                  }
                                }}>
                                <option value="">Select...</option>
                                <option value={OFFICE_EXPENSE_ID}>Office Expense</option>
                                <option disabled>──────────</option>
                                {dropdownProjects.map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
                              </select>
                            </td>
                            <td>
                              <input type="text" value={row.vendor} placeholder="Vendor"
                                style={{ fontSize: 10, padding: '2px 3px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                                onChange={e => updateEditingRow(r.id, { vendor: e.target.value })} />
                            </td>
                            <td>
                              <input type="text" value={row.description} placeholder="Description"
                                style={{ fontSize: 10, padding: '2px 3px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                                onChange={e => updateEditingRow(r.id, { description: e.target.value })} />
                            </td>
                            <td>
                              <select value={row.category}
                                style={{ fontSize: 10, padding: '2px 4px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                                onChange={e => updateEditingRow(r.id, { category: e.target.value })}>
                                <option value="">—</option>
                                {(row.projectId === OFFICE_EXPENSE_ID ? OFFICE_OVERHEAD_CATEGORIES : PROJECT_EXPENSE_CATEGORIES).map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </td>
                            <td>
                              <select value={row.officeLocation}
                                style={{ fontSize: 10, padding: '2px 3px', width: '100%', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                                onChange={e => updateEditingRow(r.id, { officeLocation: e.target.value })}>
                                <option value="NL">NL</option>
                                <option value="IN">IN</option>
                              </select>
                            </td>
                            <td className="text-center">
                              <input type="checkbox" className="brio-checkbox"
                                checked={row.clientBillable || false}
                                title="Client-billable"
                                disabled={!row.projectId || row.projectId === OFFICE_EXPENSE_ID}
                                onChange={e => updateEditingRow(r.id, { clientBillable: e.target.checked })} />
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <select value={row.currency}
                                  style={{ fontSize: 9, padding: '2px', width: 36, flexShrink: 0, border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                                  onChange={e => updateEditingRow(r.id, { currency: e.target.value })}>
                                  <option value="INR">₹</option>
                                  <option value="EUR">€</option>
                                </select>
                                <input type="text" inputMode="decimal" value={row.amount} placeholder="0"
                                  style={{ fontSize: 11, padding: '2px 4px', width: '100%', textAlign: 'right', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 3 }}
                                  onChange={e => updateEditingRow(r.id, { amount: e.target.value.replace(',', '.') })} />
                              </div>
                            </td>
                            <td className="text-center">
                              <span className={`brio-badge ${statusInfo.badge}`} style={{ fontSize: '9px', padding: '1px 5px' }}>
                                {statusInfo.label}
                              </span>
                            </td>
                            <td className="text-center">
                              {r.fileDropboxUrl ? (
                                <a href={r.fileDropboxUrl} target="_blank" rel="noopener noreferrer"
                                  title={r.fileName} style={{ color: 'var(--brio-gold)' }}>
                                  <FileText className="w-3 h-3" />
                                </a>
                              ) : row.file ? (
                                <span title={row.file.name} style={{ color: 'var(--brio-gold)' }}><FileText className="w-3 h-3" /></span>
                              ) : (
                                <span style={{ color: '#ccc', fontSize: 9 }}>—</span>
                              )}
                            </td>
                            <td className="text-center">
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                <button className="brio-btn brio-btn-primary" style={{ padding: '2px 8px', fontSize: '9px' }}
                                  onClick={() => saveRow(r.id)} disabled={row.uploading}>
                                  {row.uploading ? '...' : 'Save'}
                                </button>
                                <button className="brio-btn brio-btn-secondary" style={{ padding: '2px 6px', fontSize: '9px' }}
                                  onClick={() => cancelEditingRow(r.id)}>
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      return (
                        <tr key={r.id} className="brio-row--hoverable">
                          {showVoucherCheckbox && (
                            <td className="text-center">
                              {(r.status === 'pending' || r.status === 'approved' || r.status === 'reimbursed') && (
                                <input type="checkbox" className="brio-checkbox" checked={selectedIds.has(r.id)}
                                  onChange={(e) => toggleSelect(r.id, rowIdx, e.nativeEvent.shiftKey)} />
                              )}
                            </td>
                          )}
                          <td className="brio-text-grey" style={cellStyle}>{r.expenseDate}</td>
                          <td className="brio-text-grey" style={cellStyle} title={getUserName(r.userId)}>{getUserName(r.userId)}</td>
                          <td className="brio-text-grey" style={cellStyle} title={getProjectName(r.projectId)}>{getProjectName(r.projectId)}</td>
                          <td className="brio-text-grey" style={cellStyle} title={r.vendor}>{r.vendor}</td>
                          <td className="brio-text-grey" style={cellStyle} title={r.description}>{r.description}</td>
                          <td className="brio-text-grey" style={cellStyle}>{r.category}</td>
                          <td className="brio-text-grey" style={cellStyle}>{r.officeLocation || '—'}</td>
                          <td className="text-center" style={{ fontSize: 10 }}>
                            {r.clientBillable ? (
                              <span style={{ color: 'var(--brio-gold, #9F902A)' }} title="Client-billable">$</span>
                            ) : (
                              <span style={{ color: '#ccc' }}>—</span>
                            )}
                          </td>
                          <td className="text-right brio-font-medium">{formatMoney(displayAmt(r), displayCurrency, { exact: true })}</td>
                          <td className="text-center">
                            <span className={`brio-badge ${statusInfo.badge}`} style={{ fontSize: '9px', padding: '1px 5px' }}>
                              {statusInfo.label}
                            </span>
                            {r.reimbursedDate && (
                              <div style={{ fontSize: 8, color: 'var(--brio-grey)', marginTop: 1 }}>
                                {r.reimbursedDate} ({r.reimbursedMethod})
                              </div>
                            )}
                          </td>
                          <td className="text-center">
                            {r.fileDropboxUrl ? (
                              <a href={r.fileDropboxUrl} target="_blank" rel="noopener noreferrer"
                                title={r.fileName} style={{ color: 'var(--brio-gold)' }}>
                                <FileText className="w-3 h-3" />
                              </a>
                            ) : (
                              <span style={{ color: '#ccc', fontSize: 9 }}>—</span>
                            )}
                          </td>
                          <td className="text-center">
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                              {(r.status === 'pending' || isAdmin) && (
                                <BrioIconButton icon="edit" variant="edit" title="Edit"
                                  onClick={() => startEditRow(r)} />
                              )}
                              {isAdminOrSupervisor && r.status === 'pending' && (
                                <button className="brio-icon-btn brio-icon-btn--approve"
                                  style={{ padding: '2px', lineHeight: 0 }}
                                  onClick={async () => {
                                    const result = await updateReimbursement(r.id, { status: 'approved' });
                                    if (!result.success) alert('Error: ' + result.error);
                                  }}
                                  title="Approve">
                                  <Check size={13} />
                                </button>
                              )}
                              {isAdmin && r.status === 'approved' && activeTab === 'payouts' && r._source !== 'expense' && (
                                <button className="brio-icon-btn brio-icon-btn--approve"
                                  style={{ padding: '2px', lineHeight: 0 }}
                                  onClick={() => { setReimburseModal(r.id); setReimburseDate(new Date().toISOString().split('T')[0]); }}
                                  title="Mark as reimbursed">
                                  <Check size={13} />
                                </button>
                              )}
                              {(r.status === 'pending' || isAdmin) && (
                                <BrioIconButton icon="delete" variant="delete" title="Delete"
                                  onClick={async () => {
                                    if (!window.confirm('Delete this expense record?')) return;
                                    const result = await deleteReimbursement(r.id);
                                    if (!result.success) alert('Error: ' + result.error);
                                  }} />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </BrioTable>
              </BrioByCode>
            )}

            {/* ── Totals summary ── */}
            {filtered.length > 0 && (
              <div className="brio-flex brio-justify-between brio-items-center" style={{ marginTop: 8, padding: '4px 8px', fontSize: 11 }}>
                <span className="brio-text-grey">{filtered.length} expenses</span>
                <span className="brio-font-bold">
                  Total: {formatMoney(filtered.reduce((s, r) => s + displayAmt(r), 0), displayCurrency, { exact: true })}
                </span>
              </div>
            )}
            </>)}
          </BrioAnalyticsSurface>

          {/* ── Vouchers Section (Admin only, Payouts tab) ── */}
          {isAdmin && activeTab === 'payouts' && vouchers.length > 0 && (
            <BrioAnalyticsSurface padding="m">
              <div className="brio-flex brio-items-center brio-justify-between"
                style={{ cursor: 'pointer', marginBottom: voucherSectionExpanded ? 12 : 0 }}
                onClick={() => setVoucherSectionExpanded(!voucherSectionExpanded)}>
                <div className="brio-flex brio-items-center brio-gap-xs">
                  <span className="brio-font-bold brio-text-s">
                    Reimbursement Vouchers
                    <span style={{ fontWeight: 400, color: 'var(--brio-grey)', marginLeft: 6 }}>
                      ({vouchers.length})
                    </span>
                  </span>
                </div>
                {voucherSectionExpanded
                  ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--brio-grey)' }} />
                  : <ChevronDown className="w-4 h-4" style={{ color: 'var(--brio-grey)' }} />}
              </div>

              {voucherSectionExpanded && (
                <BrioByCode code={PRESENTATION_CODE} className="!overflow-visible">
                  <BrioTable {...tableProps} style={{ tableLayout: 'fixed', width: '100%' }}>
                    <colgroup>
                      <col style={{ width: '3%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '35%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th></th>
                        <th>Voucher ID</th>
                        <th>Date</th>
                        <th className="text-center">Items</th>
                        <th className="text-right">Total ({displaySymbol})</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vouchers.map(v => {
                        const total = v.items.reduce((s, r) => s + displayAmt(r), 0);
                        const isExpanded = expandedVoucherId === v.id;
                        const canEdit = !v.status || v.status === 'draft' || v.status === 'invoiced';
                        return (
                          <React.Fragment key={v.id}>
                            <tr className="brio-row--hoverable" style={{ cursor: 'pointer' }}
                              onClick={() => setExpandedVoucherId(isExpanded ? null : v.id)}>
                              <td style={{ padding: '4px' }}>
                                {isExpanded
                                  ? <ChevronUp className="w-3 h-3" style={{ color: 'var(--brio-grey)' }} />
                                  : <ChevronDown className="w-3 h-3" style={{ color: 'var(--brio-grey)' }} />}
                              </td>
                              <td className="brio-font-medium">{v.id}</td>
                              <td className="brio-text-grey">{v.date}</td>
                              <td className="text-center">{v.items.length}</td>
                              <td className="text-right brio-font-medium">{formatMoney(total, displayCurrency, { exact: true })}</td>
                              <td className="text-center">
                                <span className={`brio-badge ${
                                  v.status === 'paid' ? 'brio-badge--approved' :
                                  v.status === 'invoiced' ? 'brio-badge--pending' :
                                  'brio-badge--warning'
                                }`} style={{ fontSize: '9px', padding: '1px 5px' }}>
                                  {v.status || 'draft'}
                                </span>
                              </td>
                              <td className="text-center" onClick={e => e.stopPropagation()}>
                                <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                                  {(!v.status || v.status === 'draft') && (
                                    <button className="brio-btn brio-btn-primary" style={{ padding: '2px 6px', fontSize: '9px' }}
                                      onClick={async () => {
                                        await updateVoucherStatus(v.id, 'invoiced');
                                        await supabaseExpenses?.updateExpenseVoucherStatus?.(v.id, 'invoiced');
                                      }}>
                                      Mark Invoiced
                                    </button>
                                  )}
                                  {v.status === 'invoiced' && (
                                    <button className="brio-btn brio-btn-primary" style={{ padding: '2px 6px', fontSize: '9px' }}
                                      onClick={async () => {
                                        await updateVoucherStatus(v.id, 'paid');
                                        await supabaseExpenses?.updateExpenseVoucherStatus?.(v.id, 'paid');
                                      }}>
                                      Mark Paid
                                    </button>
                                  )}
                                  {canEdit && (
                                    <button className="brio-btn brio-btn-secondary" style={{ padding: '2px 6px', fontSize: '9px', color: 'var(--brio-red, #c0392b)' }}
                                      onClick={async () => {
                                        if (!window.confirm(`Delete voucher ${v.id}? All ${v.items.length} item(s) will revert to "Approved" status.`)) return;
                                        await deleteVoucher(v.id);
                                        await supabaseExpenses?.deleteExpenseVoucher?.(v.id);
                                        if (isExpanded) setExpandedVoucherId(null);
                                      }}>
                                      Delete Voucher
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {/* ── Expanded voucher detail rows ── */}
                            {isExpanded && v.items.map(r => (
                              <tr key={`${v.id}-${r.id}`} style={{ background: 'var(--brio-gold-light, #f5f3e8)' }}>
                                <td></td>
                                <td className="brio-text-grey" style={{ fontSize: 10 }}>{r.expenseDate}</td>
                                <td className="brio-text-grey" style={{ fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 }}
                                  title={getUserName(r.userId)}>{getUserName(r.userId)}</td>
                                <td className="brio-text-grey" style={{ fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 }}
                                  title={r.description || r.vendor}>{r.description || r.vendor || '—'}</td>
                                <td className="text-right" style={{ fontSize: 10 }}>{formatMoney(displayAmt(r), displayCurrency, { exact: true })}</td>
                                <td className="text-center">
                                  <span className="brio-text-grey" style={{ fontSize: 9 }}>{r.category}</span>
                                </td>
                                <td className="text-center">
                                  {canEdit && (
                                    <button className="brio-icon-btn brio-icon-btn--delete"
                                      style={{ padding: '2px', lineHeight: 0 }}
                                      title="Remove from voucher"
                                      onClick={async () => {
                                        if (!window.confirm(`Remove this item from voucher ${v.id}?`)) return;
                                        if (r._source === 'expense') {
                                          const res = await supabaseExpenses?.removeExpenseFromVoucher?.(r._expenseId);
                                          if (res && !res.success) alert('Error: ' + res.error);
                                        } else {
                                          const res = await removeFromVoucher(r.id);
                                          if (!res.success) alert('Error: ' + res.error);
                                        }
                                      }}>
                                      <X size={12} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </BrioTable>
                </BrioByCode>
              )}
            </BrioAnalyticsSurface>
          )}

          {/* ── Client Reimbursements from Payment Inbox (Admin, Payouts tab) ── */}
          {isAdmin && activeTab === 'payouts' && invoicesHook && (
            <BrioAnalyticsSurface padding="m">
              <div
                className="brio-flex brio-items-center brio-justify-between"
                style={{ cursor: 'pointer', marginBottom: expenseSectionExpanded ? 12 : 0 }}
                onClick={() => setExpenseSectionExpanded(!expenseSectionExpanded)}
              >
                <div className="brio-flex brio-items-center brio-gap-xs">
                  <Link2 className="w-4 h-4" style={{ color: 'var(--brio-gold, #c5a572)' }} />
                  <span className="brio-font-bold brio-text-s">
                    Client Reimbursements
                    <span style={{ fontWeight: 400, color: 'var(--brio-grey)', marginLeft: 6 }}>
                      ({unlinkedExpensePayments.length} unlinked, {linkedExpensePayments.length} linked)
                    </span>
                  </span>
                </div>
                {expenseSectionExpanded
                  ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--brio-grey)' }} />
                  : <ChevronDown className="w-4 h-4" style={{ color: 'var(--brio-grey)' }} />}
              </div>

              {expenseSectionExpanded && (
                <div>
                  <p className="brio-analytics-muted brio-text-xs" style={{ marginBottom: 10 }}>
                    Bank payments marked as &quot;Expense&quot; in the Payment Inbox. Assign them to a project so they appear in project cost calculations.
                  </p>

                  {expensePaymentsLoading && (
                    <div className="brio-text-center brio-analytics-muted" style={{ padding: '16px 0', fontSize: 12 }}>Loading expense payments…</div>
                  )}

                  {!expensePaymentsLoading && expensePayments.length === 0 && (
                    <div className="brio-text-center brio-analytics-muted" style={{ padding: '24px 0', fontSize: 12, color: '#999' }}>
                      No ad-hoc expense payments yet. Mark bank payments as &quot;Expense&quot; in the Payment Inbox (Projects → Invoices) to see them here.
                    </div>
                  )}

                  {/* Unlinked expense payments */}
                  {unlinkedExpensePayments.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div className="brio-font-medium brio-text-xs" style={{ marginBottom: 6, color: 'var(--brio-gold, #c5a572)' }}>
                        Unlinked ({unlinkedExpensePayments.length})
                      </div>
                      <BrioByCode code={PRESENTATION_CODE} className="!overflow-visible">
                        <BrioTable {...tableProps}>
                          <thead>
                            <tr>
                              <th>Project</th>
                              <th>Voucher</th>
                              <th>Description</th>
                              <th className="text-right">Amount ({displaySymbol})</th>
                              <th className="text-center">Date</th>
                              <th className="text-center">Status</th>
                              <th className="text-center">Office</th>
                              <th className="text-center col-actions">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unlinkedExpensePayments.map(evt => {
                              const amtDisplay = convertMoney(evt.amount, 'INR', displayCurrency, evt.txnDate);
                              return (
                                <tr key={evt.id} className="brio-row--hoverable" style={{ background: '#fffcf0' }}>
                                  <td>
                                    <select
                                      value=""
                                      className="brio-btn brio-btn-secondary"
                                      style={{ width: '100%', padding: '4px 8px', fontSize: '11px' }}
                                      onChange={(e) => {
                                        if (e.target.value) handleLinkToProject(evt.id, parseInt(e.target.value));
                                      }}
                                    >
                                      <option value="">Select project…</option>
                                      {dropdownProjects.map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
                                    </select>
                                  </td>
                                  <td>
                                    <select
                                      value={evt.linkedVoucherId || ''}
                                      className="brio-btn brio-btn-secondary"
                                      style={{ width: '100%', padding: '4px 8px', fontSize: '11px' }}
                                      onChange={(e) => handleExpPaymentFieldChange(evt.id, 'linkedVoucherId', e.target.value || null)}
                                    >
                                      <option value="">No voucher</option>
                                      {vouchers.map(v => <option key={v.id} value={v.id}>{v.id}</option>)}
                                    </select>
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={editingExpPayments[`${evt.id}_narration`] ?? evt.narration ?? ''}
                                      className="brio-btn brio-btn-secondary"
                                      style={{ width: '100%', padding: '4px 8px', fontSize: '11px' }}
                                      placeholder="Description"
                                      onChange={(e) => setEditingExpPayments(prev => ({ ...prev, [`${evt.id}_narration`]: e.target.value }))}
                                      onBlur={(e) => {
                                        const val = e.target.value;
                                        if (val !== (evt.narration ?? '')) handleExpPaymentFieldChange(evt.id, 'narration', val);
                                        setEditingExpPayments(prev => { const n = { ...prev }; delete n[`${evt.id}_narration`]; return n; });
                                      }}
                                    />
                                  </td>
                                  <td className="text-right brio-font-medium">{formatMoney(amtDisplay, displayCurrency)}</td>
                                  <td>
                                    <BrioDatePicker
                                      value={evt.txnDate || ''}
                                      onChange={(v) => handleExpPaymentFieldChange(evt.id, 'txnDate', v)}
                                      openAbove
                                      className="brio-datepicker-trigger--compact"
                                      style={{ width: '100%', maxWidth: '95px', fontSize: '10px', boxSizing: 'border-box' }}
                                    />
                                  </td>
                                  <td className="text-center">
                                    <span className="brio-badge brio-badge--warning">Unlinked</span>
                                  </td>
                                  <td>
                                    <select
                                      value={evt.expenseOffice || ''}
                                      className="brio-btn brio-btn-secondary"
                                      style={{ width: '100%', padding: '4px 8px', fontSize: '11px' }}
                                      onChange={(e) => handleExpPaymentFieldChange(evt.id, 'expenseOffice', e.target.value)}
                                    >
                                      <option value="">Select…</option>
                                      <option value="NL">NL</option>
                                      <option value="IN">IN</option>
                                    </select>
                                  </td>
                                  <td className="text-center">
                                    <BrioIconButton icon="delete" variant="delete" title="Delete expense payment"
                                      onClick={() => handleDeleteExpPayment(evt.id)}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </BrioTable>
                      </BrioByCode>
                    </div>
                  )}

                  {/* Linked expense payments */}
                  {linkedExpensePayments.length > 0 && (
                    <div>
                      <div className="brio-font-medium brio-text-xs" style={{ marginBottom: 6, color: 'var(--brio-grey)' }}>
                        Linked ({linkedExpensePayments.length})
                      </div>
                      <BrioByCode code={PRESENTATION_CODE} className="!overflow-visible">
                        <BrioTable {...tableProps}>
                          <thead>
                            <tr>
                              <th>Project</th>
                              <th>Voucher</th>
                              <th>Description</th>
                              <th className="text-right">Amount ({displaySymbol})</th>
                              <th className="text-center">Date</th>
                              <th className="text-center">Status</th>
                              <th className="text-center">Office</th>
                              <th className="text-center col-actions">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {linkedExpensePayments.map(evt => {
                              const amtDisplay = convertMoney(evt.amount, 'INR', displayCurrency, evt.txnDate);
                              return (
                                <tr key={evt.id} className="brio-row--hoverable">
                                  <td>
                                    <select
                                      value={evt.linkedProjectId || ''}
                                      className="brio-btn brio-btn-secondary"
                                      style={{ width: '100%', padding: '4px 8px', fontSize: '11px' }}
                                      onChange={(e) => {
                                        const val = e.target.value ? parseInt(e.target.value) : null;
                                        handleExpPaymentFieldChange(evt.id, 'linkedProjectId', val);
                                      }}
                                    >
                                      <option value="">Select project…</option>
                                      {dropdownProjects.map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
                                    </select>
                                  </td>
                                  <td>
                                    <select
                                      value={evt.linkedVoucherId || ''}
                                      className="brio-btn brio-btn-secondary"
                                      style={{ width: '100%', padding: '4px 8px', fontSize: '11px' }}
                                      onChange={(e) => handleExpPaymentFieldChange(evt.id, 'linkedVoucherId', e.target.value || null)}
                                    >
                                      <option value="">No voucher</option>
                                      {vouchers.map(v => <option key={v.id} value={v.id}>{v.id}</option>)}
                                    </select>
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={editingExpPayments[`${evt.id}_narration`] ?? evt.narration ?? ''}
                                      className="brio-btn brio-btn-secondary"
                                      style={{ width: '100%', padding: '4px 8px', fontSize: '11px' }}
                                      placeholder="Description"
                                      onChange={(e) => setEditingExpPayments(prev => ({ ...prev, [`${evt.id}_narration`]: e.target.value }))}
                                      onBlur={(e) => {
                                        const val = e.target.value;
                                        if (val !== (evt.narration ?? '')) handleExpPaymentFieldChange(evt.id, 'narration', val);
                                        setEditingExpPayments(prev => { const n = { ...prev }; delete n[`${evt.id}_narration`]; return n; });
                                      }}
                                    />
                                  </td>
                                  <td className="text-right brio-font-medium">{formatMoney(amtDisplay, displayCurrency)}</td>
                                  <td>
                                    <BrioDatePicker
                                      value={evt.txnDate || ''}
                                      onChange={(v) => handleExpPaymentFieldChange(evt.id, 'txnDate', v)}
                                      openAbove
                                      className="brio-datepicker-trigger--compact"
                                      style={{ width: '100%', maxWidth: '95px', fontSize: '10px', boxSizing: 'border-box' }}
                                    />
                                  </td>
                                  <td className="text-center">
                                    <span className="brio-badge brio-badge--approved">Linked</span>
                                  </td>
                                  <td>
                                    <select
                                      value={evt.expenseOffice || ''}
                                      className="brio-btn brio-btn-secondary"
                                      style={{ width: '100%', padding: '4px 8px', fontSize: '11px' }}
                                      onChange={(e) => handleExpPaymentFieldChange(evt.id, 'expenseOffice', e.target.value)}
                                    >
                                      <option value="">Select…</option>
                                      <option value="NL">NL</option>
                                      <option value="IN">IN</option>
                                    </select>
                                  </td>
                                  <td className="text-center">
                                    <BrioIconButton icon="delete" variant="delete" title="Delete expense payment"
                                      onClick={() => handleDeleteExpPayment(evt.id)}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </BrioTable>
                      </BrioByCode>
                    </div>
                  )}
                </div>
              )}
            </BrioAnalyticsSurface>
          )}
        </div>
      </BrioFrame>

      {/* ── Import History (Admin, Payouts tab) ── */}
      {/* Sits outside the gap-s content column, so add a matching top gap to
          separate it from the Client Reimbursements table above. */}
      {isAdmin && activeTab === 'payouts' && (
        <div style={{ marginTop: 'var(--brio-space-s)' }}>
          <ImportHistorySection
            statementImportsHook={statementImportsHook}
            supabaseExpenses={supabaseExpenses}
            reimbursements={reimbursements}
            activeProjects={activeProjects}
            activeUsers={activeUsers}
            formatMoney={formatMoney}
            displayCurrency={displayCurrency}
            convertMoney={convertMoney}
            fmtProject={fmtProject}
          />
        </div>
      )}

      {/* Add/Edit modal removed — editing is now inline in the table */}

      {/* ── Reimburse Modal ── */}
      {reimburseModal && (
        <BrioModal
          title="Mark as Reimbursed"
          onClose={() => setReimburseModal(null)}
          width={360}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label className="brio-text-xs brio-font-medium">Reimbursement Date</label>
              <BrioDatePicker value={reimburseDate} onChange={setReimburseDate}
                clearable={false} style={{ width: '100%' }} />
            </div>
            <div>
              <label className="brio-text-xs brio-font-medium">Method</label>
              <BrioSelect value={reimburseMethod} onChange={e => setReimburseMethod(e.target.value)}
                style={{ width: '100%' }}>
                <option value="digital">Digital</option>
                <option value="cash">Cash</option>
              </BrioSelect>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button className="brio-btn brio-btn-secondary" style={{ padding: '6px 16px', fontSize: '12px' }}
                onClick={() => setReimburseModal(null)}>
                Cancel
              </button>
              <button className="brio-btn brio-btn-primary" style={{ padding: '6px 16px', fontSize: '12px' }}
                onClick={handleReimburse}>
                <Check className="w-3 h-3" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                Confirm
              </button>
            </div>
          </div>
        </BrioModal>
      )}

      {/* ── Create Voucher Modal ── */}
      {showVoucherModal && (
        <BrioModal
          title="Create Reimbursement Voucher"
          onClose={() => setShowVoucherModal(false)}
          width={420}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p className="brio-text-xs brio-text-grey">
              Group {selectedIds.size} selected expenses into a reimbursement voucher.
              This voucher can then be invoiced to the client or processed as an expense reimbursement.
            </p>
            <div>
              <label className="brio-text-xs brio-font-medium">Selected Expenses</label>
              <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid var(--brio-border)', borderRadius: 4, padding: 8, marginTop: 4 }}>
                {Array.from(selectedIds).map(id => {
                  const r = reimbursements.find(x => x.id === id);
                  if (!r) return null;
                  return (
                    <div key={id} style={{ fontSize: 10, padding: '2px 0', borderBottom: '1px solid #f0f0f0' }}>
                      {r.expenseDate} — {getUserName(r.userId)} — {r.vendor || r.description} — {formatMoney(displayAmt(r), displayCurrency, { exact: true })}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="brio-font-medium" style={{ fontSize: 12 }}>
              Total: {formatMoney(
                Array.from(selectedIds).reduce((s, id) => {
                  const r = reimbursements.find(x => x.id === id);
                  return s + (r ? displayAmt(r) : 0);
                }, 0),
                displayCurrency,
                { exact: true }
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button className="brio-btn brio-btn-secondary" style={{ padding: '6px 16px', fontSize: '12px' }}
                onClick={() => setShowVoucherModal(false)}>
                Cancel
              </button>
              <button className="brio-btn brio-btn-primary" style={{ padding: '6px 16px', fontSize: '12px' }}
                onClick={handleCreateVoucher}>
                Create Voucher
              </button>
            </div>
          </div>
        </BrioModal>
      )}

      {/* ── Statement Import Modal ── */}
      {showImportModal && (
        <StatementImportModal
          onClose={() => { setShowImportModal(false); if (statementImportsHook) statementImportsHook.refresh(); }}
          activeProjects={activeProjects}
          teamMembers={activeUsers || []}
          supabaseExpenses={supabaseExpenses}
          formatMoney={formatMoney}
          displayCurrency={displayCurrency}
          getFxRate={settings.getFxRateForDate}
          autoRules={settings.appSettings?.statementAutoRules || {}}
          updateSetting={settings.updateSetting}
        />
      )}
    </div>
  );
}
